"use client"

import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/thumbs"

import { updateImageSrc } from "@/lib/utils"
import { Image as ImageType } from "@/types/tour"
import { Modal } from "antd"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { Navigation, Thumbs } from "swiper/modules"
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from "swiper/react"

const ModalImageGallery = ({
  images,
  isOpen,
  setIsOpen,
  clickedImage,
}: {
  images?: (ImageType | string)[]
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  clickedImage?: ImageType | string
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null)
  const mainSwiperRef = useRef<SwiperRef | null>(null)

  // ✅ FIX 1: Xử lý clickedImage - normalize dữ liệu và compare đúng cách
  useEffect(() => {
    if (!clickedImage || !images || images.length === 0 || !isOpen) return

    const index = images.findIndex(img => {
      // Trường hợp 1: clickedImage là string
      if (typeof clickedImage === "string") {
        return typeof img === "string" ? img === clickedImage : img.url === clickedImage
      }

      // Trường hợp 2: clickedImage là ImageType object
      if (typeof img === "object") {
        return img.id === clickedImage.id
      }

      return false
    })

    if (index !== -1 && mainSwiperRef.current?.swiper) {
      // Add a small delay để đảm bảo Swiper đã initialize
      setTimeout(() => {
        mainSwiperRef.current?.swiper?.slideTo(index)
        setActiveIndex(index)
      }, 100)
    }
  }, [clickedImage, images, isOpen]) // ✅ Thêm `images` và `isOpen` vào dependency

  // ✅ FIX 2: Reset state khi modal đóng
  useEffect(() => {
    if (!isOpen) {
      setThumbsSwiper(null)
      setActiveIndex(0)
    }
  }, [isOpen])

  if (!images || images.length === 0) {
    return null
  }

  const defaultStyleNavButton =
    "rounded-full bg-[#EBEDF0] sm:p-2 p-1 cursor-pointer absolute hover:scale-125 transition-all duration-300 z-20 top-1/2 -translate-y-1/2"

  // ✅ FIX 3: Handler cho next/prev - dùng ref thay vì selector string
  const handlePrev = () => {
    mainSwiperRef.current?.swiper?.slidePrev()
  }

  const handleNext = () => {
    mainSwiperRef.current?.swiper?.slideNext()
  }

  return (
    <Modal
      footer={null}
      open={isOpen}
      width={1200}
      onCancel={() => setIsOpen(false)}
      closeIcon={<X size={24} color="#999999" />}
      classNames={{
        content: "!rounded-2xl",
      }}
    >
      {isOpen && (
        <div className="max-w-[1200px] mx-auto bg-white rounded-xl p-4 sm:p-8 w-full">
          <div className="mb-4 sm:mb-8">
            <Swiper
              ref={mainSwiperRef}
              modules={[Navigation, Thumbs]}
              spaceBetween={10}
              slidesPerView={1}
              // ✅ Loại bỏ string selector, dùng handler thay thế
              navigation={false}
              onSlideChange={swiper => {
                setActiveIndex(swiper.activeIndex)
              }}
              thumbs={{
                swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
              }}
              className="main-swiper rounded-xl overflow-hidden w-full h-[40vh] sm:h-[60vh] relative"
            >
              {images.map((image, index) => {
                const imageUrl = updateImageSrc(typeof image === "string" ? image : (image as { url?: string })?.url)
                return (
                  <SwiperSlide key={typeof image === "object" ? image.id ?? index : index}>
                    <Image
                      src={imageUrl}
                      alt={
                        typeof image === "object" ? image.name ?? `photo gallery ${image.id}` : `photo gallery ${index}`
                      }
                      fill
                      className="object-contain max-w-[1000px] mx-auto"
                    />
                  </SwiperSlide>
                )
              })}

              <div className="text-sm text-gray-500 p-2 bg-gray-100 rounded-xl absolute bottom-0 left-0 right-0 w-fit z-20">
                {activeIndex + 1}/{images.length}
              </div>

              {/* ✅ FIX 4: Sửa button - dùng handler ref thay vì selector */}
              <button
                className={`${defaultStyleNavButton} left-0 sm:left-2 ${
                  activeIndex === 0 ? "opacity-50 cursor-not-allowed" : ""
                }`}
                aria-label="Previous slide"
                onClick={handlePrev}
                disabled={activeIndex === 0}
              >
                <ChevronLeft color="#368F55" className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>

              <button
                className={`${defaultStyleNavButton} right-0 sm:right-2 ${
                  activeIndex === images.length - 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
                aria-label="Next slide"
                onClick={handleNext}
                disabled={activeIndex === images.length - 1}
              >
                <ChevronRight color="#368F55" className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </Swiper>
          </div>

          {images.length > 1 && (
            <div className="w-full">
              <Swiper
                onSwiper={setThumbsSwiper}
                breakpoints={{
                  0: { spaceBetween: 4, slidesPerView: 3.5 },
                  1024: { spaceBetween: 12, slidesPerView: 4.5 },
                }}
                freeMode
                watchSlidesProgress={true}
                modules={[Thumbs]}
              >
                {images.map((image, index) => {
                  const imageUrl = updateImageSrc(typeof image === "string" ? image : (image as { url?: string })?.url)
                  return (
                    <SwiperSlide
                      key={typeof image === "object" ? image.id ?? index : index}
                      className={`relative cursor-pointer rounded-md overflow-hidden border-2 hover:border-[#368F55] transition-colors aspect-[1.345] !h-20 sm:!h-[150px] group ${
                        index === activeIndex ? "border-[#368F55]" : "border-transparent"
                      }`}
                    >
                      {index !== activeIndex && (
                        <div className="absolute inset-0 bg-black/50 z-10 group-hover:bg-black/0 transition-colors duration-200" />
                      )}

                      <Image
                        src={imageUrl}
                        alt={typeof image === "object" ? image.name ?? `thumb ${index + 1}` : `thumb ${index + 1}`}
                        fill
                        className={`object-cover group-hover:scale-105 transition-all duration-200 ${
                          index === activeIndex ? "scale-105" : "scale-100"
                        }`}
                      />
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

export default ModalImageGallery
